import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ROUTES } from '@/config/routes.ts';
import { organizerSidebar } from '@/config/sidebarConfig.ts';
import {
  deleteCompetitionRuleApi,
  exportCompetitionFfpjpXlsx,
  getCompetitionApi,
  getCompetitionRulesApi,
  publishCompetitionApi,
  updateCompetitionApi,
  uploadCompetitionCoverApi,
  upsertCompetitionRuleApi,
} from '@/lib/api/competitionsApi';
import { getOrganizationApi } from '@/lib/api/organizationsApi';
import { buildPublishConfirmMessage, publishDateErrors, reminderSkipWarning } from '@/lib/competition-messages';
import { clampLocalDateTime, localDateTimeInputToUtcIso, utcIsoToLocalDateTimeInput } from '@/lib/date-input';
import { getFileUrl } from '@/lib/file-url';
import { queryKeys } from '@/lib/query/queryKeys';
import {
  BASE_INPUT_CLASS,
  type CompetitionFormData,
  type CompetitionFormErrors,
  CompetitionFormSchema,
  type CompetitionFormState,
  LEVEL_OPTIONS,
  PLAYERS_PER_TYPE,
  SPORT_OPTIONS,
  STRIPE_FIXED_CENTS,
  STRIPE_RATE_BPS,
  TYPE_OPTIONS,
  validateCompetitionField,
} from '@/lib/validation/competitionSchema';
import { useAuthStore } from '@/stores/auth-store';
import { computeCommissionCents, slugify } from '@shared';
import {
  CompetitionLevel,
  CompetitionStatus,
  CompetitionType,
  SportType,
  StripeAccountStatus,
} from '@shared/enums/domain.enum';
import type { AddressSuggestion, Competition, CompetitionRule } from '@shared/types/entities';
import { isOrgaForfaitActive } from '@shared/utils/premium.utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, Calendar, Download, Info, Lock, Save, ScrollText, Send, Users } from 'lucide-react';
import { toast } from 'sonner';

import { CoverUploadSection } from '../../components/competition/CoverUploadSection';
import { FfpjpFiltersSection } from '../../components/competition/FfpjpFiltersSection';
import { PriceEstimation } from '../../components/competition/PriceEstimation';
import { type DraftRule, RulesEditor } from '../../components/competition/RulesEditor';
import { AddressAutocomplete } from '../../components/geo/AddressAutocomplete';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { LeafletMap } from '../../components/map/mapLeaflet';

const DEFAULT_COMPETITION_TIME = '09:00';

function getDatePart(dateTime: string): string {
  return dateTime.split('T')[0] ?? '';
}

function getTimePart(dateTime: string): string {
  return dateTime.includes('T')
    ? (dateTime.split('T')[1]?.slice(0, 5) ?? DEFAULT_COMPETITION_TIME)
    : DEFAULT_COMPETITION_TIME;
}

function combineDateAndTime(date: string, time: string): string {
  return date ? `${date}T${time || DEFAULT_COMPETITION_TIME}` : '';
}

function competitionToFormState(c: Competition): CompetitionFormState {
  return {
    name: c.name,
    alias: c.alias,
    type: c.type ?? '',
    level: c.level ?? '',
    sportType: c.sportType,
    // Conversion UTC→local pour les inputs (cf. lib/date-input.ts). Un simple
    // slice de l'ISO UTC décalerait l'heure de l'offset du fuseau à la sauvegarde.
    date: c.date ? utcIsoToLocalDateTimeInput(c.date) : '',
    registrationEnd: c.registrationEnd ? utcIsoToLocalDateTimeInput(c.registrationEnd) : '',
    registrationOpensAt: c.registrationOpensAt ? utcIsoToLocalDateTimeInput(c.registrationOpensAt) : '',
    maxTeams: c.maxTeams !== null ? String(c.maxTeams) : '',
    minTeams: c.minTeams !== null ? String(c.minTeams) : '',
    playersPerTeam: c.playersPerTeam ?? 3,
    // price stocké en centimes → euros pour l'UI
    price: c.price !== null ? String(c.price / 100) : '',
    venue: c.venue ?? '',
    description: c.description ?? '',
    restrictedToMembers: c.restrictedToMembers,
    restrictedAffiliates: c.restrictedAffiliates,
    requireTeamName: c.requireTeamName ?? true,
    addressLabel: c.address ?? '',
    city: c.city ?? '',
    department: c.department ?? '',
    postcode: c.postcode ?? '',
    citycode: c.citycode ?? '',
    latitude: c.latitude,
    longitude: c.longitude,
    allowedAgeCategories: c.allowedAgeCategories ?? [],
    allowedClassifications: c.allowedClassifications ?? [],
  };
}

function rulesToDraft(rules: CompetitionRule[]): DraftRule[] {
  return rules.map(r => ({ id: r.id, content: r.content, type: r.type, isVisible: r.isVisible, order: r.order }));
}

// ─── Skeleton ─────────────────────────────────────────────
function FormSkeleton() {
  return (
    <div className='mx-auto max-w-4xl animate-pulse space-y-6 sm:space-y-8'>
      {[1, 2, 3].map(i => (
        <div key={i} className='rounded-2xl border border-slate-200 bg-white p-4 sm:p-8'>
          <div className='mb-6 h-6 w-48 rounded bg-slate-200' />
          <div className='space-y-4'>
            <div className='h-12 rounded-lg bg-slate-100' />
            <div className='h-12 rounded-lg bg-slate-100' />
            <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
              <div className='h-12 rounded-lg bg-slate-100' />
              <div className='h-12 rounded-lg bg-slate-100' />
              <div className='h-12 rounded-lg bg-slate-100' />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Form (reçoit la compétition + règles déjà chargées) ──
function CompetitionEditForm({
  competition,
  initialRules,
}: {
  competition: Competition;
  initialRules: CompetitionRule[];
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore(s => s.user);
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const organizationId = user?.organizationsAdmin[0]?.organizationId;

  const baseInputClass = 'w-full rounded-lg border px-4 py-3 focus:border-blue-500 focus:outline-none';

  const { data: orgData } = useQuery({
    queryKey: queryKeys.organizations.detail(organizationId ?? 0),
    queryFn: () => getOrganizationApi(organizationId!),
    enabled: !!organizationId && isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });

  // Initialisation dérivée des props — pas de useEffect nécessaire
  const [form, setForm] = useState<CompetitionFormState>(() => competitionToFormState(competition));
  const [aliasManual, setAliasManual] = useState(false);
  const [errors, setErrors] = useState<CompetitionFormErrors>({});
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(() =>
    competition.files?.[0] ? getFileUrl(competition.files[0]) : null,
  );
  const [rules, setRules] = useState<DraftRule[]>(() => rulesToDraft(initialRules));
  const [deletedRuleIds, setDeletedRuleIds] = useState<number[]>([]);
  const [exportingFfpjp, setExportingFfpjp] = useState(false);

  const authOrg = user?.organizationsAdmin[0]?.organization;
  const forfaitActive = isOrgaForfaitActive(authOrg?.plan, authOrg?.subscriptionStatus);

  const priceCents = form.price !== '' ? Math.round(Number(form.price) * 100) : 0;
  const isCommission = !forfaitActive && priceCents > 0;
  const bcFeeCents = isCommission ? computeCommissionCents(priceCents) : 0;
  const stripeFeeCents = priceCents > 0 ? Math.round((priceCents * STRIPE_RATE_BPS) / 10_000) + STRIPE_FIXED_CENTS : 0;
  const netOrgaCents = Math.max(0, priceCents - bcFeeCents - stripeFeeCents);
  const stripeNotActive = orgData?.stripeAccountStatus !== StripeAccountStatus.ACTIVE;
  // Verrou prix C2 (miroir UI du backend) : le tarif est figé dès que le
  // concours n'est plus un brouillon OU qu'au moins une équipe est inscrite.
  const priceLocked = competition.status !== CompetitionStatus.DRAFT || competition.currentTeams > 0;

  // ─── Helpers ──────────────────────────────────────────
  function set<K extends keyof CompetitionFormState>(key: K, value: CompetitionFormState[K]) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  function fieldClass(key: keyof CompetitionFormErrors) {
    return `${BASE_INPUT_CLASS} ${errors[key] ? 'border-red-400 bg-red-50' : 'border-slate-200'}`;
  }

  function handleBlur(key: keyof CompetitionFormData, value: unknown) {
    const msg = validateCompetitionField(key, value);
    setErrors(prev => ({ ...prev, [key]: msg }));

    // Cohérence cross-field : la clôture est cappée à l'instant de début du
    // concours (datetime). Si l'utilisateur saisit une clôture après le coup
    // d'envoi (même jour ou plus tard), on la ramène à l'heure du concours.
    if (key === 'registrationEnd' || key === 'date') {
      const regEndVal = key === 'registrationEnd' ? String(value) : form.registrationEnd;
      const compDateVal = key === 'date' ? String(value) : form.date;

      if (regEndVal && compDateVal) {
        const capped = clampLocalDateTime(regEndVal, compDateVal);
        setErrors(prev => ({ ...prev, registrationEnd: undefined }));
        if (capped !== regEndVal) {
          set('registrationEnd', capped);
          toast.info('Clôture des inscriptions ajustée à l’heure de début du concours');
        }
      }
    }
  }

  function handleNameChange(name: string) {
    set('name', name);
    if (!aliasManual) set('alias', slugify(name));
  }

  function handleAliasChange(alias: string) {
    setAliasManual(true);
    set('alias', alias);
  }

  function handleTypeChange(type: CompetitionType) {
    set('type', type);
    set('playersPerTeam', PLAYERS_PER_TYPE[type]);
  }

  function handleCoverChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  }

  function buildPayload() {
    if (!form.type || !form.level) return null;
    return {
      name: form.name.trim(),
      alias: form.alias.trim(),
      type: form.type,
      level: form.level,
      sportType: form.sportType,
      date: localDateTimeInputToUtcIso(form.date),
      registrationEnd: localDateTimeInputToUtcIso(form.registrationEnd),
      // null = effacer la date d'ouverture (le champ vide libère l'ouverture immédiate)
      registrationOpensAt: form.registrationOpensAt ? localDateTimeInputToUtcIso(form.registrationOpensAt) : null,
      maxTeams: Number(form.maxTeams),
      minTeams: form.minTeams !== '' ? Number(form.minTeams) : undefined,
      playersPerTeam: form.playersPerTeam,
      price: form.price !== '' ? Math.round(Number(form.price) * 100) : undefined,
      venue: form.venue.trim(),
      address: form.addressLabel.trim() || undefined,
      description: form.description.trim() || undefined,
      restrictedToMembers: form.restrictedToMembers,
      restrictedAffiliates: form.restrictedAffiliates,
      requireTeamName: form.requireTeamName,
      city: form.city || undefined,
      department: form.department || undefined,
      postcode: form.postcode || undefined,
      citycode: form.citycode || undefined,
      latitude: form.latitude ?? undefined,
      longitude: form.longitude ?? undefined,
      allowedAgeCategories: form.allowedAgeCategories,
      allowedClassifications: form.allowedClassifications,
    };
  }

  async function handleExportFfpjp() {
    setExportingFfpjp(true);
    try {
      await exportCompetitionFfpjpXlsx(competition.id, competition.alias);
    } catch {
      toast.error("Erreur lors de l'export FFPJP");
    } finally {
      setExportingFfpjp(false);
    }
  }

  function handleRulesChange(newRules: DraftRule[]) {
    // Détecte les règles existantes supprimées pour les envoyer à l'API sur save
    const removedIds = rules.filter(r => r.id && !newRules.find(nr => nr.id === r.id)).map(r => r.id!);
    if (removedIds.length > 0) setDeletedRuleIds(prev => [...prev, ...removedIds]);
    setRules(newRules);
  }

  function validate(): boolean {
    const result = CompetitionFormSchema.safeParse(form);
    if (!result.success) {
      const fieldErrors = result.error.flatten().fieldErrors;
      const newErrors: CompetitionFormErrors = {};
      for (const [k, msgs] of Object.entries(fieldErrors)) {
        if (msgs?.[0]) newErrors[k as keyof CompetitionFormErrors] = msgs[0];
      }
      setErrors(newErrors);
      toast.error('Veuillez corriger les erreurs avant de continuer');
      return false;
    }
    setErrors({});
    return true;
  }

  function handleSubmit() {
    if (!validate()) return;
    saveMutation.mutate();
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = buildPayload();
      if (!payload) throw new Error('Formulaire incomplet');

      const updated = await updateCompetitionApi(competition.id, payload);

      if (coverFile) await uploadCompetitionCoverApi(competition.id, coverFile);

      // Upsert toutes les règles non vides (ordre = index + 1)
      const filledRules = rules.filter(r => r.content.trim());
      for (let i = 0; i < filledRules.length; i++) {
        const r = filledRules[i];
        await upsertCompetitionRuleApi(competition.id, {
          id: r.id,
          content: r.content,
          type: r.type,
          isVisible: r.isVisible,
          order: i + 1,
        });
      }

      // Supprimer les règles retirées
      await Promise.all(deletedRuleIds.map(id => deleteCompetitionRuleApi(id)));

      return updated;
    },
    onSuccess: () => {
      toast.success('Concours modifié avec succès');
      void queryClient.invalidateQueries({ queryKey: queryKeys.competitions.all() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.competitions.rules(competition.id) });
      void queryClient.invalidateQueries({ queryKey: ['org-capabilities', organizationId] });
      navigate(ROUTES.COMPETITION_LIST);
    },
  });

  // Publication d'un brouillon — validation gated côté API (POST /competitions/:id/publish).
  const publishMutation = useMutation({
    mutationFn: () => publishCompetitionApi(competition.id),
    onSuccess: () => {
      toast.success('Concours publié — inscriptions ouvertes');
      void queryClient.invalidateQueries({ queryKey: queryKeys.competitions.all() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.competitions.detail(competition.id) });
      void queryClient.invalidateQueries({ queryKey: ['org-capabilities', organizationId] });
      navigate(ROUTES.COMPETITION_LIST);
    },
    onError: (err: unknown) => {
      // Le backend renvoie { message, missingFields: string[] } sur 400 quand des champs requis manquent.
      const anyErr = err as {
        body?: { missingFields?: string[]; message?: string };
        response?: { data?: { missingFields?: string[]; message?: string } };
        message?: string;
      };
      const data = anyErr?.body ?? anyErr?.response?.data;
      if (data?.missingFields?.length) {
        toast.error(`Champs requis manquants : ${data.missingFields.join(', ')}`);
      } else {
        toast.error(data?.message ?? anyErr?.message ?? 'Erreur lors de la publication');
      }
    },
  });

  // ─── Unsaved-changes guard ─────────────────────────────
  const initialFormState = useMemo(() => competitionToFormState(competition), [competition]);

  const isDirty = useMemo(() => {
    const formDirty = (Object.keys(initialFormState) as (keyof CompetitionFormState)[]).some(k => {
      const a = form[k];
      const b = initialFormState[k];
      // Arrays (allowedAgeCategories, allowedClassifications) — compare serialized
      if (Array.isArray(a) && Array.isArray(b)) return JSON.stringify(a) !== JSON.stringify(b);
      return a !== b;
    });

    const rulesDirty =
      deletedRuleIds.length > 0 ||
      rules.some(r => {
        if (!r.id) return true; // new rule
        const orig = initialRules.find(ir => ir.id === r.id);
        if (!orig) return true;
        return (
          r.content !== orig.content || r.type !== orig.type || r.isVisible !== orig.isVisible || r.order !== orig.order
        );
      });

    return formDirty || rulesDirty || !!coverFile;
  }, [form, initialFormState, rules, deletedRuleIds, initialRules, coverFile]);

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Avertissement rappels Temporal : si la date est < 7j (J-7) ou < 1j (J-1), le
  // workflow skippera le(s) rappel(s) email. Affiché uniquement en DRAFT (la date
  // est verrouillée après publication). Recalculé à chaque rendu (dépend de now).
  const dateReminderWarning = competition.status === CompetitionStatus.DRAFT ? reminderSkipWarning(form.date) : null;

  return (
    <>
      {/* Header (responsive) */}
      <div className='mb-6 sm:mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
        <div className='flex items-start gap-3 sm:gap-4 min-w-0'>
          <Link
            to={ROUTES.COMPETITION_LIST}
            className='rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 shrink-0'
            onClick={e => {
              if (
                isDirty &&
                !window.confirm('Vous avez des modifications non sauvegardées. Quitter sans sauvegarder ?')
              )
                e.preventDefault();
            }}
            aria-label='Retour'
          >
            <ArrowLeft className='h-5 w-5' />
          </Link>

          <div className='min-w-0'>
            <h1 className='text-2xl sm:text-3xl font-extrabold text-slate-900 truncate'>
              Modifier : {competition.name}
            </h1>
            <p className='mt-1 text-slate-500'>Modifiez les informations de votre concours</p>
          </div>
        </div>

        <div className='flex flex-col sm:flex-row gap-3 w-full sm:w-auto'>
          <button
            type='button'
            disabled={exportingFfpjp}
            onClick={() => void handleExportFfpjp()}
            className='w-full sm:w-auto flex items-center justify-center gap-2 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:opacity-50'
          >
            <Download className='h-4 w-4' />
            {exportingFfpjp ? 'Export…' : 'Export FFPJP'}
          </button>

          {competition.status === CompetitionStatus.DRAFT && (
            <button
              type='button'
              disabled={publishMutation.isPending || saveMutation.isPending}
              onClick={() => {
                if (isDirty) {
                  toast.warning("Enregistrez d'abord vos modifications avant de publier");
                  return;
                }
                // Garde « dans le futur » spécifique à la publication (miroir API).
                const dateErrors = publishDateErrors(competition.date, competition.registrationEnd);
                if (dateErrors.date || dateErrors.registrationEnd) {
                  toast.error(dateErrors.date ?? dateErrors.registrationEnd);
                  return;
                }
                if (!window.confirm(buildPublishConfirmMessage(competition.date))) return;
                publishMutation.mutate();
              }}
              title={isDirty ? "Enregistrez d'abord vos modifications" : 'Publier ce brouillon'}
              className='w-full sm:w-auto flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-6 py-2 text-sm font-bold text-white shadow-lg shadow-emerald-200 transition hover:bg-emerald-700 disabled:opacity-50'
            >
              <Send className='h-4 w-4' />
              {publishMutation.isPending ? 'Publication…' : 'Publier'}
            </button>
          )}

          <button
            type='button'
            disabled={saveMutation.isPending}
            onClick={handleSubmit}
            className='w-full sm:w-auto flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-2 text-sm font-bold text-white shadow-lg shadow-blue-200 transition hover:bg-blue-700 disabled:opacity-50'
          >
            <Save className='h-4 w-4' />
            {saveMutation.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>

      <div className='mx-auto max-w-4xl space-y-6 sm:space-y-8'>
        {/* Section 1 — Informations de base */}
        <div className='rounded-2xl border border-slate-200 bg-white p-4 sm:p-8'>
          <h2 className='mb-6 flex items-center gap-2 text-lg font-bold text-slate-900'>
            <Info className='h-5 w-5 text-blue-600' />
            Informations de base
          </h2>

          <div className='space-y-6'>
            <div>
              <label className='mb-2 block text-sm font-semibold text-slate-700'>
                Nom du concours <span className='text-red-600'>*</span>
              </label>
              <input
                type='text'
                value={form.name}
                onChange={e => handleNameChange(e.target.value)}
                onBlur={e => handleBlur('name', e.target.value)}
                placeholder='Ex: Concours National de Lyon'
                className={fieldClass('name')}
              />
              {errors.name && <p className='mt-1 text-xs text-red-500'>{errors.name}</p>}
            </div>

            <div>
              <label className='mb-2 block text-sm font-semibold text-slate-700'>
                Alias (URL) <span className='text-red-600'>*</span>
              </label>
              <input
                type='text'
                value={form.alias}
                onChange={e => handleAliasChange(e.target.value)}
                onBlur={e => handleBlur('alias', e.target.value)}
                placeholder='concours-national-lyon'
                className={fieldClass('alias')}
              />
              {errors.alias ? (
                <p className='mt-1 text-xs text-red-500'>{errors.alias}</p>
              ) : (
                <p className='mt-1 text-xs text-slate-400'>Modifiable — utilisé dans les URLs publiques.</p>
              )}
            </div>

            <div>
              <label className='mb-2 block text-sm font-semibold text-slate-700'>Description</label>
              <textarea
                rows={3}
                value={form.description}
                onChange={e => set('description', e.target.value)}
                placeholder='Décrivez votre concours...'
                className={`${baseInputClass} border-slate-200`}
              />
            </div>

            <div className='grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6'>
              <div>
                <label className='mb-2 block text-sm font-semibold text-slate-700'>
                  Type <span className='text-red-600'>*</span>
                </label>
                <select
                  value={form.type}
                  onChange={e => handleTypeChange(e.target.value as CompetitionType)}
                  onBlur={e => handleBlur('type', e.target.value || undefined)}
                  className={fieldClass('type')}
                >
                  <option value=''>Sélectionner</option>
                  {TYPE_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                {errors.type && <p className='mt-1 text-xs text-red-500'>{errors.type}</p>}
              </div>

              <div>
                <label className='mb-2 block text-sm font-semibold text-slate-700'>
                  Niveau <span className='text-red-600'>*</span>
                </label>
                <select
                  value={form.level}
                  onChange={e => set('level', e.target.value as CompetitionLevel)}
                  onBlur={e => handleBlur('level', e.target.value || undefined)}
                  className={fieldClass('level')}
                >
                  <option value=''>Sélectionner</option>
                  {LEVEL_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                {errors.level && <p className='mt-1 text-xs text-red-500'>{errors.level}</p>}
              </div>

              <div>
                <label className='mb-2 block text-sm font-semibold text-slate-700'>
                  Sport <span className='text-red-600'>*</span>
                </label>
                <select
                  value={form.sportType}
                  onChange={e => set('sportType', e.target.value as SportType)}
                  className={`${baseInputClass} border-slate-200`}
                >
                  {SPORT_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Section 2 — Date et lieu */}
        <div className='rounded-2xl border border-slate-200 bg-white p-4 sm:p-8'>
          <h2 className='mb-6 flex items-center gap-2 text-lg font-bold text-slate-900'>
            <Calendar className='h-5 w-5 text-emerald-600' />
            Date et lieu
          </h2>

          <div className='space-y-6'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6'>
              <div>
                <label className='mb-2 block text-sm font-semibold text-slate-700'>
                  Date et heure du concours <span className='text-red-600'>*</span>
                </label>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                  <div>
                    <label className='mb-1 block text-xs font-semibold text-slate-500'>Date</label>
                    <input
                      type='date'
                      value={getDatePart(form.date)}
                      onChange={e => set('date', combineDateAndTime(e.target.value, getTimePart(form.date)))}
                      onBlur={e => handleBlur('date', combineDateAndTime(e.target.value, getTimePart(form.date)))}
                      className={fieldClass('date')}
                    />
                  </div>
                  <div>
                    <label className='mb-1 block text-xs font-semibold text-slate-500'>Heure</label>
                    <input
                      type='time'
                      value={getTimePart(form.date)}
                      onChange={e => set('date', combineDateAndTime(getDatePart(form.date), e.target.value))}
                      onBlur={e => handleBlur('date', combineDateAndTime(getDatePart(form.date), e.target.value))}
                      className={fieldClass('date')}
                    />
                  </div>
                </div>
                {errors.date && <p className='mt-1 text-xs text-red-500'>{errors.date}</p>}
                {!errors.date && dateReminderWarning && (
                  <p className='mt-1 text-xs text-amber-600'>⚠️ {dateReminderWarning}</p>
                )}
              </div>

              <div>
                <label className='mb-2 block text-sm font-semibold text-slate-700'>
                  Clôture des inscriptions <span className='text-red-600'>*</span>
                </label>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                  <div>
                    <label className='mb-1 block text-xs font-semibold text-slate-500'>Date</label>
                    <input
                      type='date'
                      value={getDatePart(form.registrationEnd)}
                      onChange={e =>
                        set('registrationEnd', combineDateAndTime(e.target.value, getTimePart(form.registrationEnd)))
                      }
                      onBlur={e =>
                        handleBlur(
                          'registrationEnd',
                          combineDateAndTime(e.target.value, getTimePart(form.registrationEnd)),
                        )
                      }
                      className={fieldClass('registrationEnd')}
                    />
                  </div>
                  <div>
                    <label className='mb-1 block text-xs font-semibold text-slate-500'>Heure</label>
                    <input
                      type='time'
                      value={getTimePart(form.registrationEnd)}
                      onChange={e =>
                        set('registrationEnd', combineDateAndTime(getDatePart(form.registrationEnd), e.target.value))
                      }
                      onBlur={e =>
                        handleBlur(
                          'registrationEnd',
                          combineDateAndTime(getDatePart(form.registrationEnd), e.target.value),
                        )
                      }
                      className={fieldClass('registrationEnd')}
                    />
                  </div>
                </div>
                {errors.registrationEnd && <p className='mt-1 text-xs text-red-500'>{errors.registrationEnd}</p>}
              </div>
            </div>

            {/* Feature 7 (2026-08) — date d'ouverture des inscriptions (optionnelle) */}
            <div>
              <label className='mb-2 block text-sm font-semibold text-slate-700'>
                Date d&apos;ouverture des inscriptions <span className='font-normal text-slate-400'>(optionnel)</span>
              </label>
              <input
                type='date'
                value={getDatePart(form.registrationOpensAt)}
                onChange={e => set('registrationOpensAt', e.target.value ? `${e.target.value}T00:01` : '')}
                onBlur={e => handleBlur('registrationOpensAt', e.target.value ? `${e.target.value}T00:01` : '')}
                className={fieldClass('registrationOpensAt')}
              />
              <p className='mt-1 text-xs text-amber-600'>
                ⚠️ Si renseignée, les inscriptions resteront <strong>bloquées jusqu&apos;à cette date</strong> (à
                00h01), même si le concours est publié. Laissez vide pour ouvrir les inscriptions dès la publication.
              </p>
              {errors.registrationOpensAt && <p className='mt-1 text-xs text-red-500'>{errors.registrationOpensAt}</p>}
            </div>

            <div className='space-y-3'>
              <div>
                <label className='mb-2 block text-sm font-semibold text-slate-700'>
                  Nom du lieu <span className='text-red-600'>*</span>
                </label>
                <input
                  type='text'
                  value={form.venue}
                  onChange={e => set('venue', e.target.value)}
                  onBlur={e => handleBlur('venue', e.target.value)}
                  placeholder='Ex: Boulodrome Municipal de Lyon'
                  className={fieldClass('venue')}
                />
                {errors.venue && <p className='mt-1 text-xs text-red-500'>{errors.venue}</p>}
              </div>

              <div>
                <label className='mb-2 block text-sm font-semibold text-slate-700'>
                  Adresse (pour la géolocalisation)
                </label>
                <AddressAutocomplete
                  value={form.addressLabel}
                  onChange={v => set('addressLabel', v)}
                  onSelect={(s: AddressSuggestion) => {
                    setForm(prev => ({
                      ...prev,
                      addressLabel: s.label,
                      city: s.city ?? '',
                      department: s.context?.split(', ')[1] ?? '',
                      postcode: s.postcode ?? '',
                      citycode: s.citycode ?? '',
                      latitude: s.latitude,
                      longitude: s.longitude,
                    }));
                  }}
                  placeholder="Rechercher l'adresse du lieu…"
                />
                {form.latitude && form.longitude && (
                  <p className='mt-1 text-xs text-emerald-600'>
                    📍 {form.city} ({form.postcode}) — {form.latitude.toFixed(5)}, {form.longitude.toFixed(5)}
                  </p>
                )}
              </div>
            </div>

            <div className='rounded-3xl border border-slate-200 bg-white p-4 sm:p-6'>
              <div className='relative flex aspect-video items-center justify-center rounded-2xl bg-linear-to-br from-slate-200 to-slate-300 overflow-hidden'>
                <LeafletMap lat={form.latitude ?? undefined} lng={form.longitude ?? undefined} />
              </div>
            </div>
          </div>
        </div>

        {/* Section 3 — Inscriptions */}
        <div className='rounded-2xl border border-slate-200 bg-white p-4 sm:p-8'>
          <h2 className='mb-6 flex items-center gap-2 text-lg font-bold text-slate-900'>
            <Users className='h-5 w-5 text-purple-600' />
            Inscriptions
          </h2>

          <div className='space-y-6'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6'>
              <div>
                <label className='mb-2 block text-sm font-semibold text-slate-700'>
                  Équipes max <span className='text-red-600'>*</span>
                </label>
                <input
                  type='number'
                  min={2}
                  value={form.maxTeams}
                  onChange={e => set('maxTeams', e.target.value)}
                  onBlur={e => handleBlur('maxTeams', e.target.value)}
                  placeholder='Ex: 64'
                  className={fieldClass('maxTeams')}
                />
                {errors.maxTeams && <p className='mt-1 text-xs text-red-500'>{errors.maxTeams}</p>}
              </div>

              <div>
                <label className='mb-2 block text-sm font-semibold text-slate-700'>Équipes min</label>
                <input
                  type='number'
                  min={2}
                  value={form.minTeams}
                  onChange={e => set('minTeams', e.target.value)}
                  onBlur={e => handleBlur('minTeams', e.target.value)}
                  placeholder='Ex: 8'
                  className={fieldClass('minTeams')}
                />
                {errors.minTeams ? (
                  <p className='mt-1 text-xs text-red-500'>{errors.minTeams}</p>
                ) : (
                  <p className='mt-1 text-xs text-slate-400'>Facultatif</p>
                )}
              </div>
            </div>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6'>
              <div>
                <label className='mb-2 block text-sm font-semibold text-slate-700'>Joueurs par équipe</label>
                <input
                  type='number'
                  value={form.playersPerTeam}
                  readOnly
                  className='w-full cursor-not-allowed rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-slate-500'
                />
                <p className='mt-1 text-xs text-slate-400'>Déduit du type de concours</p>
              </div>

              <div>
                <label className='mb-2 block text-sm font-semibold text-slate-700'>Prix par équipe (€)</label>
                <div className='relative'>
                  <input
                    type='number'
                    min={0}
                    step='0.01'
                    value={form.price}
                    onChange={e => set('price', e.target.value)}
                    onBlur={e => handleBlur('price', e.target.value)}
                    placeholder='Ex: 30'
                    disabled={priceLocked}
                    className={`${fieldClass('price')}${priceLocked ? ' cursor-not-allowed bg-slate-50 text-slate-500' : ''}`}
                  />
                  <span className='absolute right-4 top-1/2 -translate-y-1/2 font-semibold text-slate-400'>€</span>
                </div>

                {errors.price ? (
                  <p className='mt-1 text-xs text-red-500'>{errors.price}</p>
                ) : priceLocked ? (
                  <p className='mt-1 flex items-start gap-1 text-xs text-slate-500'>
                    <Lock className='mt-0.5 h-3 w-3 shrink-0' />
                    Tarif figé : le concours est publié ou des équipes sont déjà inscrites. Clonez-le en brouillon pour
                    proposer un autre prix.
                  </p>
                ) : (
                  <p className='mt-1 text-xs text-slate-400'>Laisser vide pour gratuit</p>
                )}

                {priceCents > 0 && stripeNotActive && (
                  <div className='mt-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3'>
                    <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0 text-amber-500' />
                    <p className='text-xs text-amber-800'>
                      Votre compte Stripe n&apos;est pas encore actif. Les joueurs ne pourront pas payer pour ce
                      concours. Activez votre compte Stripe dans le tableau de bord.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <PriceEstimation
              priceCents={priceCents}
              isCommission={isCommission}
              bcFeeCents={bcFeeCents}
              stripeFeeCents={stripeFeeCents}
              netOrgaCents={netOrgaCents}
            />

            <div className='flex flex-col gap-2'>
              <div className='relative flex items-center gap-2 group'>
                <input
                  type='checkbox'
                  id='restricted'
                  checked={form.restrictedToMembers}
                  onChange={e => set('restrictedToMembers', e.target.checked)}
                  className='h-5 w-5 cursor-pointer rounded border-slate-300 text-blue-600 focus:ring-blue-500 shrink-0'
                />
                <label htmlFor='restricted' className='cursor-pointer text-sm font-semibold text-slate-700'>
                  Réservé aux membres licenciés
                </label>
                <span className='pointer-events-none absolute left-0 bottom-full mb-2 w-72 rounded-md bg-slate-800 px-3 py-2 text-xs text-white shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-20'>
                  Seuls les joueurs avec une licence fédérale valide peuvent s&apos;inscrire à ce concours.
                </span>
              </div>

              <div className='relative flex items-center gap-2 group'>
                <input
                  type='checkbox'
                  id='restrictedAffiliate'
                  checked={form.restrictedAffiliates}
                  onChange={e => set('restrictedAffiliates', e.target.checked)}
                  className='h-5 w-5 cursor-pointer rounded border-slate-300 text-blue-600 focus:ring-blue-500 shrink-0'
                />
                <label htmlFor='restrictedAffiliate' className='cursor-pointer text-sm font-semibold text-slate-700'>
                  Réservé aux membres des clubs affiliés
                </label>
                <span className='pointer-events-none absolute left-0 bottom-full mb-2 w-72 rounded-md bg-slate-800 px-3 py-2 text-xs text-white shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-20'>
                  L&apos;inscription est réservée aux joueurs membres d&apos;un club affilié à votre organisation.
                </span>
              </div>

              <div className='relative flex items-center gap-2 group'>
                <input
                  type='checkbox'
                  id='requireTeamName'
                  checked={form.requireTeamName}
                  onChange={e => set('requireTeamName', e.target.checked)}
                  className='h-5 w-5 cursor-pointer rounded border-slate-300 text-blue-600 focus:ring-blue-500 shrink-0'
                />
                <label htmlFor='requireTeamName' className='cursor-pointer text-sm font-semibold text-slate-700'>
                  Nom d&apos;équipe obligatoire à l&apos;inscription
                </label>
                <span className='pointer-events-none absolute left-0 bottom-full mb-2 w-80 rounded-md bg-slate-800 px-3 py-2 text-xs text-white shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-20'>
                  Décochez pour permettre l&apos;inscription sans nom d&apos;équipe (un nom sera généré
                  automatiquement). À venir : les capitaines pourront enregistrer leur équipe favorite pour pré-remplir
                  automatiquement leurs futures inscriptions.
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Section FFPJP — Filtres fédéraux */}
        <FfpjpFiltersSection
          allowedAgeCategories={form.allowedAgeCategories}
          allowedClassifications={form.allowedClassifications}
          onAgeCategoriesChange={cats => set('allowedAgeCategories', cats)}
          onClassificationsChange={cls => set('allowedClassifications', cls)}
        />

        {/* Section 4 — Règles du concours */}
        <div className='rounded-2xl border border-slate-200 bg-white p-4 sm:p-8'>
          <h2 className='mb-2 flex items-center gap-2 text-lg font-bold text-slate-900'>
            <ScrollText className='h-5 w-5 text-indigo-600' />
            Règles du concours
          </h2>
          <p className='mb-6 text-sm text-slate-500'>
            Affichées aux joueurs sur la page d&apos;inscription publique. Vous pouvez masquer une règle sans la
            supprimer (icône œil).
          </p>
          <RulesEditor rules={rules} onChange={handleRulesChange} />
        </div>

        {/* Section 5 — Affiche du concours */}
        <CoverUploadSection coverPreview={coverPreview} coverFile={coverFile} onCoverChange={handleCoverChange} />

        {/* Bottom actions (responsive) */}
        <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 py-6'>
          <Link
            to={ROUTES.COMPETITION_LIST}
            className='w-full sm:w-auto px-6 py-3 text-sm font-semibold text-slate-700 transition hover:text-slate-900 text-center'
            onClick={e => {
              if (
                isDirty &&
                !window.confirm('Vous avez des modifications non sauvegardées. Quitter sans sauvegarder ?')
              )
                e.preventDefault();
            }}
          >
            Annuler
          </Link>

          <button
            type='button'
            disabled={saveMutation.isPending}
            onClick={handleSubmit}
            className='w-full sm:w-auto flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-blue-200 transition hover:bg-blue-700 disabled:opacity-50'
          >
            <Save className='h-4 w-4' />
            {saveMutation.isPending ? 'Enregistrement…' : 'Enregistrer les modifications'}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Loader (gère loading/error avant de passer au form) ───
export default function CompetitionEdit() {
  const { id } = useParams<{ id: string }>();
  const competitionId = Number(id);

  // On attend isAuthenticated avant de lancer la query rules pour éviter le 401 au premier rendu.
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);

  const {
    data: competition,
    isLoading: compLoading,
    isError,
  } = useQuery({
    queryKey: queryKeys.competitions.detail(competitionId),
    queryFn: () => getCompetitionApi(competitionId),
    enabled: !isNaN(competitionId),
  });

  const { data: existingRules, isLoading: rulesLoading } = useQuery({
    queryKey: queryKeys.competitions.rules(competitionId),
    queryFn: () => getCompetitionRulesApi(competitionId),
    enabled: !isNaN(competitionId) && isAuthenticated,
  });

  // isLoading = true tant que : competition non chargée, auth non prête, ou règles non chargées
  const isLoading = compLoading || !isAuthenticated || rulesLoading;

  if (isError) {
    return (
      <DashboardLayout sidebarConfig={organizerSidebar}>
        <div className='flex min-h-screen flex-col items-center justify-center gap-4 p-6'>
          <p className='text-red-600'>Concours introuvable.</p>
          <Link to={ROUTES.COMPETITION_LIST} className='text-sm text-slate-600 underline'>
            Retour à la liste
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout sidebarConfig={organizerSidebar}>
      <div className='p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto'>
        {isLoading || !competition ? (
          <>
            <div className='mb-6 sm:mb-8 h-10 w-56 sm:w-72 animate-pulse rounded-lg bg-slate-200' />
            <FormSkeleton />
          </>
        ) : (
          <CompetitionEditForm competition={competition} initialRules={existingRules ?? []} />
        )}
      </div>
    </DashboardLayout>
  );
}
